// PATH: src/components/catalog/ProgramPanel.tsx
import * as React from "react";
import { Box, Button, Paper, Stack, Switch, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import { useProgramsApi, useStagesApi, useSubjectsApi } from "../../api";
import type { CurriculumProgram, Stage, Subject } from "../../models/domain";
import { tokens } from "../../theme/theme";
import { StatusPill } from "../ui";
import { useActivation } from "../activation/useActivation";
import { cascadeLines, normalizeText, plural, programCounts, stageCounts, subjectColor } from "./catalogModel";
import { useCatalog } from "./CatalogContext";
import { ProgramDialog, type ProgramTab } from "./forms/ProgramDialog";
import { SimpleEntityDialog } from "./forms/SimpleEntityDialog";
import { SubjectDialog, type SubjectTab } from "./forms/SubjectDialog";
import { DetailHeader, Facts, ListRow, Rank, Section, SectionEmpty } from "./parts";
import { ColorDot, PlanetButton, PlanetPreview } from "./PlanetPreview";
import { useCatalogDelete } from "./useCatalogDelete";
import { useDialogState } from "./useDialogState";

type DialogState =
  | { type: "program"; tab: ProgramTab }
  | { type: "stage"; stage: Stage | null }
  | { type: "subject"; subject: Subject | null; tab?: SubjectTab };

const Swatch: React.FC<{ color?: string | null; label: string }> = ({ color, label }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
    <ColorDot color={color} size={14} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography variant="body2" className="mono" sx={{ lineHeight: 1.3 }}>
        {color || "—"}
      </Typography>
    </Box>
  </Stack>
);

const Thumb: React.FC<{ url?: string | null; label: string }> = ({ url, label }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
    <Box
      component={url ? "a" : "span"}
      href={url || undefined}
      target="_blank"
      rel="noreferrer"
      title={url || undefined}
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
        color: "text.disabled",
        bgcolor: tokens.surfaceMuted,
        border: `1px solid ${tokens.border}`,
        backgroundImage: url ? `url("${url}")` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {!url && <ImageIcon sx={{ fontSize: 16 }} />}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
        {url ? "Définie" : "Aucune"}
      </Typography>
    </Box>
  </Stack>
);

export const ProgramPanel: React.FC<{ node: CurriculumProgram }> = ({ node }) => {
  const { index, select, reload } = useCatalog();
  const { toggleSubject } = useActivation();
  const [toggling, setToggling] = React.useState<string | null>(null);
  const { deleteProgram } = useProgramsApi();
  const { createStage, updateStage, deleteStage } = useStagesApi();
  const { deleteSubject } = useSubjectsApi();
  const remove = useCatalogDelete();
  const dlg = useDialogState<DialogState>();
  const dialog = dlg.state;
  const [subjectQuery, setSubjectQuery] = React.useState("");

  const p = node.program;
  const stages = node.stages ?? [];
  const subjects = node.subjects ?? [];
  const counts = programCounts(node);
  const q = normalizeText(subjectQuery);
  const shownSubjects = q
    ? subjects.filter((s) => normalizeText(s.name).includes(q) || normalizeText(s.code).includes(q))
    : subjects;
  const close = dlg.close;

  const toggleOpen = async (s: Subject) => {
    setToggling(s.id);
    try {
      if (await toggleSubject(s, !s.is_active)) await reload();
    } finally {
      setToggling(null);
    }
  };

  const deleteThisProgram = () =>
    void remove({
      entity: "program",
      label: `le programme « ${p.name} »`,
      cascade: cascadeLines(index, { kind: "program", id: p.id }),
      chapters: { program_id: p.id },
      typeToConfirm: p.code,
      remove: () => deleteProgram(p.id),
    });

  return (
    <Stack spacing={2.5}>
      <DetailHeader
        kind="Programme"
        crumbs={[]}
        title={p.name}
        code={p.code}
        leading={<PlanetPreview look={p} size={34} />}
        badges={p.is_active ? <StatusPill tone="success">Actif</StatusPill> : <StatusPill>Inactif</StatusPill>}
        actions={
          <Button variant="outlined" startIcon={<EditIcon />} onClick={() => dlg.show({ type: "program", tab: "identity" })}>
            Modifier
          </Button>
        }
        onDelete={deleteThisProgram}
        deleteLabel="Supprimer le programme"
      />

      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ p: 2.5 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Identité
          </Typography>
          <Facts
            items={[
              { label: "Code", value: <span className="mono">{p.code}</span> },
              { label: "Pays", value: p.country || "—" },
              { label: "Langue", value: p.locale || "—" },
              { label: "Rang", value: p.sort_order },
              { label: "Étapes", value: counts.stages },
              { label: "Niveaux", value: counts.grades },
              { label: "Matières", value: counts.subjects },
              { label: "Offres", value: counts.offerings },
            ]}
          />
        </Box>
        <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${tokens.border}`, bgcolor: tokens.surfaceMuted }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">Univers 3D</Typography>
            <Button size="small" onClick={() => dlg.show({ type: "program", tab: "visuals" })}>
              Modifier les visuels
            </Button>
          </Stack>
          <Stack direction="row" spacing={2.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <PlanetPreview look={p} size={52} />
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "grid",
                gap: 2,
                gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(5, minmax(0, 1fr))" },
              }}
            >
              <Swatch color={p.color_primary} label="Principale" />
              <Swatch color={p.color_secondary} label="Secondaire" />
              <Thumb url={p.texture_url} label="Texture" />
              <Thumb url={p.bump_url} label="Relief" />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.3 }}>
                  Anneaux · orbite
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
                  {p.has_rings ? "Oui" : "Non"} · {p.orbit_radius ?? "—"}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Paper>

      <Section
        title="Étapes"
        description="Les grandes périodes de la scolarité, dans l'ordre (primaire, collège, lycée…)."
        count={stages.length}
        addLabel="Ajouter une étape"
        onAdd={() => dlg.show({ type: "stage", stage: null })}
        flush
      >
        {stages.length === 0 ? (
          <SectionEmpty title="Aucune étape" description="Ajoutez la première étape de ce programme, puis ses niveaux." />
        ) : (
          stages.map((s) => {
            const c = stageCounts(s);
            return (
              <ListRow
                key={s.stage.id}
                lead={<Rank n={s.stage.sort_order} />}
                primary={s.stage.name}
                code={s.stage.code}
                secondary={`${plural(c.grades, "niveau", "niveaux", "aucun niveau")} · ${plural(c.offerings, "offre", undefined, "aucune offre")}`}
                onOpen={() => select({ kind: "stage", id: s.stage.id })}
                openLabel={`Ouvrir l'étape ${s.stage.name}`}
                onEdit={() => dlg.show({ type: "stage", stage: s.stage })}
                onDelete={() =>
                  void remove({
                    entity: "stage",
                    label: `l'étape « ${s.stage.name} »`,
                    cascade: cascadeLines(index, { kind: "stage", id: s.stage.id }),
                    chapters: { program_id: p.id, stage_id: s.stage.id },
                    typeToConfirm: s.stage.code,
                    remove: () => deleteStage(s.stage.id),
                  })
                }
              />
            );
          })
        )}
      </Section>

      <Section
        title="Matières du programme"
        description="Le répertoire des matières ; chaque niveau choisit ensuite celles qu'il propose. L'interrupteur ouvre ou ferme une matière et tous ses cours aux élèves."
        count={subjects.length}
        addLabel="Ajouter une matière"
        onAdd={() => dlg.show({ type: "subject", subject: null })}
        filter={
          subjects.length > 8
            ? { value: subjectQuery, onChange: setSubjectQuery, placeholder: "Filtrer les matières" }
            : undefined
        }
        flush
      >
        {subjects.length === 0 ? (
          <SectionEmpty title="Aucune matière" description="Ajoutez les matières, puis proposez-les aux niveaux." />
        ) : shownSubjects.length === 0 ? (
          <SectionEmpty title="Aucune matière ne correspond" />
        ) : (
          shownSubjects.map((s) => {
            const used = index.gradesBySubject.get(s.id) ?? [];
            return (
              <ListRow
                key={s.id}
                lead={
                  <PlanetButton
                    look={s}
                    fallbackColor={subjectColor(s)}
                    label={s.name}
                    onClick={() => dlg.show({ type: "subject", subject: s, tab: "visuals" })}
                  />
                }
                primary={s.name}
                code={s.code}
                dimmed={!s.is_active}
                secondary={
                  (s.is_active ? "" : "Fermée aux élèves · ") +
                  (used.length === 0
                    ? "Proposée à aucun niveau"
                    : `Proposée en ${used
                        .slice(0, 6)
                        .map((g) => g.grade.grade.code)
                        .join(", ")}${used.length > 6 ? ` et ${used.length - 6} autres` : ""}`)
                }
                meta={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      rang {s.sort_order}
                    </Typography>
                    <Tooltip title={s.is_active ? "Ouverte aux élèves — fermer" : "Fermée aux élèves — ouvrir"}>
                      <Switch
                        size="small"
                        checked={s.is_active}
                        disabled={toggling === s.id}
                        onChange={() => void toggleOpen(s)}
                        inputProps={{ "aria-label": `Matière ${s.name} ouverte aux élèves` }}
                      />
                    </Tooltip>
                  </Stack>
                }
                onEdit={() => dlg.show({ type: "subject", subject: s })}
                onDelete={() =>
                  void remove({
                    entity: "subject",
                    label: `la matière « ${s.name} »`,
                    cascade: cascadeLines(index, { kind: "subject", programId: p.id, id: s.id }),
                    chapters: { program_id: p.id, subject_id: s.id },
                    typeToConfirm: s.code,
                    remove: () => deleteSubject(s.id),
                  })
                }
              />
            );
          })
        )}
      </Section>

      <ProgramDialog
        open={dlg.isOpen("program")}
        onClose={close}
        program={p}
        initialTab={dialog?.type === "program" ? dialog.tab : "identity"}
      />
      <SimpleEntityDialog
        open={dlg.isOpen("stage")}
        onClose={close}
        entity="stage"
        title={dialog?.type === "stage" && dialog.stage ? "Modifier l'étape" : "Nouvelle étape"}
        context={`Programme : ${p.name}`}
        initial={dialog?.type === "stage" ? dialog.stage : null}
        siblings={stages.map((s) => s.stage)}
        orderUnique
        namePlaceholder="Collège"
        codePlaceholder="COLLEGE"
        successMessage={dialog?.type === "stage" && dialog.stage ? "Étape enregistrée." : "Étape créée."}
        onSubmit={async (v) => {
          const payload = { program_id: p.id, code: v.code, name: v.name, sort_order: v.sort_order };
          if (dialog?.type === "stage" && dialog.stage) await updateStage(dialog.stage.id, payload);
          else await createStage(payload);
        }}
      />
      <SubjectDialog
        open={dlg.isOpen("subject")}
        onClose={close}
        program={p}
        siblings={subjects}
        subject={dialog?.type === "subject" ? dialog.subject : null}
        initialTab={dialog?.type === "subject" ? dialog.tab : undefined}
      />
    </Stack>
  );
};

export default ProgramPanel;
